/**
 * Type definitions for the new Draft API (V2)
 * These types align with the backend's scalable draft system
 */

/**
 * Enum representing all supported draft types in the system
 */
export enum DraftType {
  LEGAL_BRIEF = "legal_brief",
  SUPPORT_LETTER = "support_letter",
  RFE_RESPONSE = "rfe_response",
  NOIR_NOID_RESPONSE = "noir_noid_response",
  EXPERT_LETTER = "expert_letter",
  EXHIBIT_LIST = "exhibit_list",
  JOB_DESCRIPTION = "job_description",
  NARRATIVE_RECRUITMENT_REPORT = "narrative_recruitment_report",
  EMPLOYMENT_VERIFICATION_LETTER = "employment_verification_letter",
  NEWSPAPER_AD = "newspaper_ad",
  SWA_JOB_ORDER = "swa_job_order",
  NOTICE_OF_FILING = "notice_of_filing",
  PERSONAL_STATEMENT = "personal_statement",
  ASYLUM_AFFIDAVIT = "asylum_affidavit",
  HARDSHIP_STATEMENT_BENEFICIARY = "hardship_statement_beneficiary",
  HARDSHIP_STATEMENT_PETITIONER = "hardship_statement_petitioner",
  COVER_LETTER = "cover_letter",
  CERTIFIED_STATEMENT = "certified_statement",
  /** Freeform draft authored entirely in Athena (no template generation). */
  CUSTOM = "custom"
}

/**
 * Status enum for draft generation lifecycle
 */
export enum GenerationStatus {
  NOT_STARTED = "Not Started",
  GENERATING = "Generating",
  GENERATED = "Generated",
  NEEDS_UPDATE = "Needs Update",
  FAILED = "Failed",
  IN_PROGRESS = "In Progress",
  /** Held until the key fact analyses it argues from land (20 minutes at most). */
  WAITING_FOR_ANALYSIS = "Waiting for analysis"
}

/** Why an agent run failed (`caseblink.drafting.failures` on the backend). */
export type AgentFailureCode =
  | "no_changes_needed"
  | "review_locked"
  | "citation_refused"
  | "context_too_large"
  | "contract_failed"
  | "mount_failed"
  | "no_document"
  | "delivery_failed"
  | "missing_record"
  | "bad_instruction"
  | "timeout"
  | "budget_exhausted"
  | "runtime_lost"
  | "cancelled"
  | "model_error";

export interface AgentFailure {
  code: AgentFailureCode;
  title?: string | null;
  message?: string | null;
  /** Whether starting the same run again can help. */
  retryable?: boolean;
  /** What the run got to: verify findings, its last message. */
  partial?: Record<string, string | number | boolean> | null;
  at?: string | null;
}

/** One agent run on a draft (`caseblink.drafting.run_history`). */
export interface AgentRun {
  run_id?: string | null;
  revise?: boolean;
  started_at?: string | null;
  ended_at?: string | null;
  outcome?: "running" | "generated" | "failed" | "cancelled" | null;
}

export interface DraftActivity {
  activity: string;
  phase?: string | null;
  turns?: number | null;
  at?: string | null;
  started_at?: string | null;
}

/** One line of a drafted document's quality report. */
export interface QualityItem {
  /** `must`: an issue the letter does not answer; `should`: worth a look. */
  severity: "must" | "should";
  text: string;
  /** The notice issue's bookmark, to jump to its section. */
  anchor?: string | null;
}

/**
 * What the drafting run left short after its evidence-plan, scorecard and
 * review gates (`caseblink.drafting.quality`).
 */
export interface QualityReport {
  /** "2 issues answered without evidence"; null when nothing is left. */
  headline?: string | null;
  headline_kind?: "without_evidence" | "unanswered" | "findings" | null;
  items: QualityItem[];
  /** The gates that sent the run back once before it delivered. */
  retried?: string[];
  at?: string | null;
}

/** A key fact analysis that landed after the draft was written. */
export interface AvailableAnalysis {
  key: string;
  label?: string | null;
  memo_draft_id?: string | null;
  short_answer?: string | null;
  landed_at?: string | null;
}

/**
 * Represents a single section within a draft document
 */
export interface DraftSection {
  /** Display name shown in the UI */
  display_name: string;
  /** Plain text content of the section */
  text: string;
  /** HTML-formatted content of the section */
  html: string;
  /** Internal section identifier (optional, used for targeted regeneration) */
  section_name?: string;
  /** Additional metadata specific to this section */
  section_metadata?: Record<string, any>;
  /** Current generation status */
  status?: GenerationStatus;
}

/**
 * Main Draft entity representing a generated document
 */
export interface Draft {
  /** Unique identifier for the draft */
  draft_id: string;
  /** ID of the individual/beneficiary this draft belongs to */
  individual_id: string;
  /** Type of draft document */
  draft_type: DraftType;
  /** Visa type (e.g., EB1A, O1A, NIW) */
  visa_type?: string;
  /** Visa subtype for additional specificity */
  visa_subtype?: string;
  /** The key fact a research memo analyses (an attached analysis). */
  fact_key?: string | null;
  /** The research catalog topic a memo answers (`start_research(topic_id=…)`). */
  research_topic?: string | null;
  /** The question a research memo was started with. */
  research_question?: string | null;
  /** Optional custom title for the draft */
  title?: string;
  /** Current generation status */
  status: GenerationStatus;
  /** Array of sections that make up the draft */
  sections: DraftSection[];
  /**
   * GCS path of the draft's docx. This is the draft: the preview, the download,
   * and the packet all render these bytes. Absent until something has authored
   * a document.
   */
  docx_storage_path?: string | null;
  /**
   * Plain-text readback of the docx, so search and agents can read a draft
   * without unzipping it. Derived, never authoritative.
   */
  content_text?: string;
  /**
   * Tracked changes (`w:ins`/`w:del`) still awaiting review in the docx. Kept on
   * every save so lists and cards can badge a draft without opening the file.
   */
  pending_changes?: number;
  /** Optional custom template ID to use for generation */
  template_id?: string;
  /** Whether to use the default template for this draft type */
  use_default_template: boolean;
  /** Flag indicating if this draft was migrated from legacy system */
  is_migrated: boolean;
  /** Timestamp when draft was created */
  created_at?: string;
  /** Timestamp when draft was last updated */
  updated_at?: string;
  /** Optional generation call ID for tracking async operations */
  generate_call_id?: string;
  /**
   * Why the last agent run ended `Failed`, as the run's harness reported it
   * (or as the backend inferred it). Cleared when a new run starts.
   */
  agent_failure_reason?: string | null;
  /** The same failure, typed by the harness or the reconciler. */
  agent_failure?: AgentFailure | null;
  /** Each agent run on this draft, newest last. */
  agent_runs?: AgentRun[] | null;
  /**
   * What the workspace shows, derived by the backend from the stored status,
   * the document and the latest run (a draft with content reads `ready`).
   */
  display_status?:
    | "idle"
    | "waiting"
    | "running"
    | "ready"
    | "stale"
    | "failed"
    | null;
  /** The latest run's failure; null once a later run is going or landed. */
  display_failure?:
    | (Omit<Partial<AgentFailure>, "code"> & {
        code?: AgentFailureCode | null;
      })
    | null;
  /**
   * While a run goes: what the drafting agent last said it was doing
   * ("Writing Criterion 3"), and when that run started. Null otherwise.
   */
  display_activity?: DraftActivity | null;
  /** Key fact analyses still running when this draft was written. */
  pending_analyses?: string[] | null;
  /** Analyses that landed since: New analysis available. */
  analyses_available?: AvailableAnalysis[] | null;
  /** While `WAITING_FOR_ANALYSIS`: the key facts it waits on, and since when. */
  waiting_for_analyses?: string[] | null;
  /** `{key: label}` for `waiting_for_analyses` ("Awards", "Judging"). */
  waiting_labels?: Record<string, string> | null;
  waiting_since?: string | null;
  /** Why a draft that waited started without an analysis. */
  start_note?: string | null;
  /** A notice response: the Notice Brief it was written from. */
  notice_brief_ref?: {
    fingerprint: string;
    notice_key: string;
    label: string;
  } | null;
  /** ...and how it answers each of the brief's issues. */
  notice_scorecard?: NoticeScorecard | null;
  /** What the run left short, for the editor's quality banner. */
  quality_report?: QualityReport | null;
  /** `"legacy"` when Classic wrote it. */
  generation_engine?: string | null;
  /** The firm's legacy template Classic used; null is CaseBlink's built-in one. */
  classic_template_id?: string | null;
  classic_letterhead_id?: string | null;
  /** Research topic keys switched off for Classic's last run; Retry keeps them. */
  classic_omitted_research?: string[] | null;
  /** Set when the picked letterhead couldn't be applied and the draft went out without it. */
  classic_letterhead_error?: string | null;
  /** The current Classic run's stage and research topics. */
  classic_progress?: ClassicProgress | null;
  /** On the draft list, for allowlisted firms only: Classic can write this draft. */
  classic_available?: boolean;
  /** Expert ID for expert letter drafts */
  expert_id?: string;
  /** EVL form ID for employment verification letter drafts */
  employment_verification_letter_form_id?: string;
  /** Additional options specific to this draft type */
  options?: Record<string, any>;
  /** Additional metadata specific to this draft */
  metadata?: Record<string, any>;
}

/**
 * Returns true when a draft should be treated as actively generating.
 *
 * Affected-section regeneration tracks progress at the section level
 * (`DraftSection.status`) while the draft-level `status` can briefly read
 * `GENERATED` (between sections, or due to read races). Checking only the
 * draft-level status causes the UI to drop the generating state and stop
 * polling before the sections finish, so callers must consider both.
 */
export const isDraftGenerating = (draft: Draft): boolean =>
  draft.status === GenerationStatus.GENERATING ||
  (draft.sections ?? []).some(
    (section) => section.status === GenerationStatus.GENERATING
  );

/**
 * User profile information attached to version records
 * (mirrors backend UserProfile model)
 */
export interface UserProfile {
  uid: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  /**
   * New backend source-of-truth: base64 data URL (e.g. "data:image/jpeg;base64,...")
   */
  avatar_data_url?: string | null;
}

/**
 * What triggered the creation of a draft version.
 */
export enum DraftVersionTrigger {
  FULL_GENERATE = "full_generate",
  SECTION_REGENERATE = "section_regenerate",
  PRE_RESTORE = "pre_restore",
  RESTORE = "restore",
  MANUAL_SECTIONS_EDIT = "manual_sections_edit",
  WORD_UPLOAD = "word_upload"
}

/**
 * Strongly-typed snapshot payload stored in draft version documents.
 */
export interface DraftVersionPayload {
  sections: DraftSection[];
  status: GenerationStatus;
  template_id?: string | null;
  use_default_template: boolean;
  title?: string | null;
  docx_storage_path?: string | null;
  content_text?: string | null;
}

/**
 * Strongly-typed version document stored at `draft/{draft_id}/versions/{version_id}`.
 *
 * Note: the API currently also injects `created_by_profile` for convenience.
 */
export interface DraftVersion {
  version_id: string;
  draft_id: string;
  individual_id: string;
  created_at: string;
  /** Last save folded into this version (an editing session); absent if none. */
  updated_at?: string | null;
  created_by?: string | null;
  created_by_profile?: UserProfile | null;
  trigger: DraftVersionTrigger;
  /** Who made it; `created_by` is whose session an agent's edit ran in. */
  actor_kind?: DraftVersionActor | null;
  operation?: DraftVersionOperation | null;
  section_name?: string | null;
  generate_call_id?: string | null;
  draft_payload: DraftVersionPayload;
  [key: string]: unknown;
}

export type DraftVersionActor =
  | "attorney"
  | "casey"
  | "drafting_agent"
  | "system";

export type DraftVersionOperation =
  | "generation"
  | "edit"
  | "tracked_proposal"
  | "accept"
  | "reject"
  | "resolve"
  | "restore"
  | "pre_restore"
  | "upload"
  | "sync";

/**
 * Request payload for creating a new draft
 */
export interface CreateDraftRequest {
  /** ID of the individual/beneficiary */
  individual_id: string;
  /** Type of draft to create */
  draft_type: DraftType;
  /** Optional visa type */
  visa_type?: string;
  /** Optional visa subtype */
  visa_subtype?: string;
  /** Optional custom title */
  title?: string;
  /** Optional pre-populated sections */
  sections?: DraftSection[];
  /** Optional custom template ID */
  template_id?: string;
  /** Whether to use default template (defaults to true) */
  use_default_template?: boolean;
  /** Flag for migration purposes */
  is_migrated?: boolean;
  /** Additional options */
  options?: Record<string, any>;
  /** Expert ID for expert letter drafts */
  expert_id?: string;
}

/**
 * Request payload for updating an existing draft
 */
export interface UpdateDraftRequest {
  /** Optional access enabled flag */
  accessEnabled?: boolean;
  /** Optional new title */
  title?: string;
  /** Optional status update */
  status?: GenerationStatus;
  /** Optional sections update (full replacement) */
  sections?: DraftSection[];
  /** Plain-text readback of the draft's docx. */
  content_text?: string;
  /** Optional template change */
  template_id?: string;
  /** Optional default template flag change */
  use_default_template?: boolean;
  /** Optional visa type update */
  visa_type?: string;
  /** Optional visa subtype update */
  visa_subtype?: string;
  /** Optional options update */
  options?: Record<string, any>;
}

/**
 * Request payload for generating or regenerating draft content
 */
export interface GenerateDraftRequest {
  /** ID of the draft to generate (for regeneration) */
  draft_id: string;
  /** Optional custom template ID */
  template_id?: string;
  /** Whether to use async generation (for long-running operations) */
  use_async?: boolean;
  /** Additional generation options */
  options?: DraftGenerationOptions;
  /** Whether to use default template */
  use_default_template?: boolean;
}

/**
 * Options for customizing draft generation behavior
 */
export interface DraftGenerationOptions {
  /** Tone of the generated content (e.g., "formal", "persuasive") */
  tone?: string;
  /** Length preference (e.g., "concise", "detailed") */
  length?: string;
  /** Creativity level (e.g., "conservative", "creative") */
  creativity?: string;
  /** Greeting style for letters */
  greeting?: string;
  /** Whether to reference individual's resume */
  reference_resume?: boolean;
  /** Expert ID (for expert letters) */
  expert_id?: string;
  /** Whether this is a support letter variant */
  is_support_letter?: boolean;
  /** Reference to a previous case */
  from_previous_case?: boolean;
  /** Custom template path (legacy support) */
  template_path?: string;
  /**
   * How exhibits should be referenced in the generated draft:
   * "cite" (default), "placeholder" (leave a fillable "(Exhibit [[—]])" token),
   * or "none" (omit exhibit citations entirely).
   */
  exhibit_reference_mode?: "cite" | "placeholder" | "none";
  /** Any additional custom options */
  [key: string]: any;
}

/**
 * Query parameters for listing drafts
 */
export interface ListDraftsParams {
  /** Filter by individual ID */
  individual_id: string;
  /** Optional filter by draft type */
  draft_type?: DraftType;
  /** Optional filter by status */
  status?: GenerationStatus;
  /** Optional filter by visa type */
  visa_type?: string;
  /** Optional filter by expert ID (for expert letter drafts) */
  expert_id?: string;
  /** Maximum number of results to return */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
}

/**
 * Response from generate endpoint (synchronous)
 */
export interface GenerateDraftResponse {
  /** The generated/updated draft */
  draft: Draft;
  /** Unique ID for tracking this generation call */
  generate_call_id: string;
}

/**
 * Response from generate-async endpoint
 */
/** `ready` is a topic with research to use (run before, or just now). */
export type ClassicTopicStatus =
  | "ready"
  | "queued"
  | "waiting"
  | "running"
  | "empty"
  | "failed"
  | "blocked"
  | "omitted";

export type ClassicProgress = {
  run_id: string;
  stage: "researching" | "writing" | "done" | "failed";
  started_at?: string | null;
  topics?: Record<
    string,
    {
      title: string;
      status: ClassicTopicStatus;
      reason?: string | null;
      /** Per-file topics' heading ("Journal Impact"); absent for case topics. */
      group?: string | null;
      /** Case files a case topic reads (the reputation topics). */
      files?: number | null;
    }
  >;
  warnings?: string[] | null;
  error?: string | null;
};

export interface GenerateDraftAsyncResponse {
  /** Success message */
  message: string;
  /** Unique ID for tracking this async generation call */
  generate_call_id: string;
  /** The draft ID that's being generated */
  draft_id: string;
}

/**
 * What kind of tracked change a review row represents. Mirrors the docx
 * engine's `changes list` kinds; `insert`/`delete`/`replace` are inline text,
 * the rest are structural.
 */
export type DocxChangeKind =
  | "insert"
  | "delete"
  | "replace"
  | "formatting"
  | "paragraphFormatting"
  | "insertParagraph"
  | "deleteParagraph"
  | "insertParagraphMark"
  | "deleteParagraphMark"
  | "insertRow"
  | "deleteRow"
  | "tableFormatting"
  | "sectionFormatting"
  | (string & Record<never, never>);

/**
 * One tracked change awaiting review, as `GET /api/draft/:id/changes` and the
 * agent's `review` payload describe it. `id` is what accept/reject take; `ids`
 * lists every revision element the decision covers.
 *
 * Text fields (`before`, `after`, `text`, `paragraph.*`) carry exhibit
 * citations as `⟦cite:<nodeId>[@p<page>]|<label>⟧` markers, where the label is
 * what the document read at the time. `splitCitationTokens` in
 * `features/draftCitations` renders them; `stripCitationMarkers` flattens them
 * back to the visible text.
 */
export interface DocxChange {
  id: string;
  ids?: string[];
  kind: DocxChangeKind;
  author?: string;
  date?: string;
  story?: string;
  paraId?: string;
  paragraphIndex?: number;
  /** Text (or formatting summary) removed by the change; empty for pure insertions. */
  before?: string | Record<string, unknown> | null;
  /** Text (or formatting summary) added by the change; empty for pure deletions. */
  after?: string | Record<string, unknown> | null;
  /** The characters the change covers, for the row summary. */
  text?: string;
  /** Surrounding sentence for context, when the engine derived one. */
  context?: string;
  /** Human summary of a formatting change, e.g. "+italic". */
  description?: string;
  /** The whole paragraph before and after the change. */
  paragraph?: { before?: string; after?: string } | null;
}

export interface DraftChangesResponse {
  draft_id: string;
  changes: DocxChange[];
  pending_changes: number;
}

export interface ResolveDraftChangesRequest {
  accept?: string[];
  reject?: string[];
  /** Short-circuit to every pending change (optionally narrowed by `author`). */
  all?: "accept" | "reject";
  author?: string;
}

export interface ResolveDraftChangesResponse {
  draft_id: string;
  accepted: string[];
  rejected: string[];
  remaining: number;
  version_id?: string | null;
  draft: Draft;
}

/** One hit from `GET /api/draft/:id/find`. */
export interface DocxFindMatch {
  story: string;
  paraId: string;
  paragraphIndex?: number;
  start: number;
  end: number;
  text: string;
  context?: string;
}

export interface DraftFindResponse {
  draft_id: string;
  query: string;
  matches: DocxFindMatch[];
  count: number;
}

/**
 * Helper type for draft type display names
 */
export const DRAFT_TYPE_LABELS: Record<DraftType, string> = {
  [DraftType.EXPERT_LETTER]: "Expert Letter",
  [DraftType.LEGAL_BRIEF]: "Attorney Legal Brief",
  [DraftType.SUPPORT_LETTER]: "Petitioner Support Letter",
  [DraftType.COVER_LETTER]: "Cover Letter",
  [DraftType.EXHIBIT_LIST]: "Exhibit List",
  [DraftType.JOB_DESCRIPTION]: "Job Description",
  [DraftType.NARRATIVE_RECRUITMENT_REPORT]: "Narrative Recruitment Report",
  [DraftType.EMPLOYMENT_VERIFICATION_LETTER]: "Employment Verification Letter",
  [DraftType.NEWSPAPER_AD]: "Newspaper Ad",
  [DraftType.SWA_JOB_ORDER]: "SWA Job Order",
  [DraftType.NOTICE_OF_FILING]: "Notice of Filing",
  [DraftType.PERSONAL_STATEMENT]: "Personal Statement",
  [DraftType.ASYLUM_AFFIDAVIT]: "Affidavit",
  [DraftType.HARDSHIP_STATEMENT_BENEFICIARY]:
    "Hardship Statement (Beneficiary)",
  [DraftType.HARDSHIP_STATEMENT_PETITIONER]: "Hardship Statement (Petitioner)",
  [DraftType.RFE_RESPONSE]: "RFE Response",
  [DraftType.NOIR_NOID_RESPONSE]: "NOID/NOIR Response",
  [DraftType.CERTIFIED_STATEMENT]: "Source of Funds Statement",
  [DraftType.CUSTOM]: "Custom"
};

/** A notice response against the brief it was written from, per issue. */
export type IssueScore = {
  key: string;
  anchor: string;
  title: string;
  /** `needs_evidence`: the letter answers it; the record still has gaps. */
  status: "answered" | "needs_evidence" | "needs_work" | "missing";
  findings: string[];
};

/** The letter answers the issue (evidence gaps are the client's to fill). */
export const answersIssue = (score: Pick<IssueScore, "status">): boolean =>
  score.status === "answered" || score.status === "needs_evidence";

export type NoticeScorecard = {
  schema_version: number;
  fingerprint: string;
  checked: boolean;
  issues: IssueScore[];
  findings: string[];
};
